import type { Campaign, Variant } from '~/modules/campaign/types';
import type { TemplateInfo } from '~/types/template';

import {
  parseTemplateIdBy,
  parseTemplateInfoBy,
  convertTemplateTypeToGPV6,
  convertTemplateTypeToGPV7,
  buildQueryParamsInAppForV6,
  buildQueryParamsInAppForV7,
} from '~/utils';
import { useQueryShopPlatform, useQueryThemeInfo } from '~/modules/apps';
import { useViewEditorShopify } from '~/hooks/useViewEditorShopify';
import { useLocationHandlers } from '~/hooks/useLocationHandlers';
import { useThemePageStore } from '~/stores/themePage';
import { ProviderSource } from '~/types/template';

export const useHandleEditTemplate = (redirect = true) => {
  const themePages = useThemePageStore((state) => state.themePages);

  const { themeId } = useQueryThemeInfo();
  const { isInstallGemPages } = useQueryShopPlatform();
  const { handleOpenPageGPInNewTab } = useLocationHandlers();
  const { handleEditTemplateShopify } = useViewEditorShopify();

  const handleEditTemplateV7 = ({ templateInfo }: { templateInfo: TemplateInfo }) => {
    if (!templateInfo) return;

    const templateId = parseTemplateIdBy(templateInfo.suffix, ProviderSource.GEMPAGESV7);
    if (!templateId || !themeId) return;

    const pageType = convertTemplateTypeToGPV7(templateInfo.type);
    const url = buildQueryParamsInAppForV7({
      pageId: templateId,
      version: 'v7',
      pageType: pageType,
      type: 'gempagesv7',
      themeId: themeId,
    });

    redirect && handleOpenPageGPInNewTab(url);
  };

  const handleEditTemplateV6 = ({ templateInfo }: { templateInfo: TemplateInfo }) => {
    if (!templateInfo) return;

    const templateId = parseTemplateIdBy(templateInfo.suffix, ProviderSource.GEMPAGESV6);
    if (!templateId || !themeId) return;

    const pageType = convertTemplateTypeToGPV6(templateInfo.type);
    const url = buildQueryParamsInAppForV6({
      pageId: templateId,
      pageType: pageType,
      version: 'v6',
    });

    redirect && handleOpenPageGPInNewTab(url);
  };

  const onEditWithTemplate = (variant: Variant, campaign?: Campaign) => {
    if (!variant?.templateSuffix) return;

    const templateInfo = parseTemplateInfoBy({
      templateSuffix: variant?.templateSuffix,
      themePages,
    });
    if (!templateInfo) return;

    const providerSource = templateInfo.providerSource;
    const isTemplateV7 = providerSource === ProviderSource.GEMPAGESV7;
    if (isTemplateV7 && isInstallGemPages) {
      handleEditTemplateV7({ templateInfo });
      return;
    }

    const isTemplateV6 = providerSource === ProviderSource.GEMPAGESV6;
    if (isTemplateV6) {
      handleEditTemplateV6({ templateInfo });
      return;
    }

    handleEditTemplateShopify(templateInfo, variant, campaign);
  };

  const onEditWithTemplateWithGPBy = (templateSuffix: string) => {
    if (!templateSuffix) return;

    const templateInfo = parseTemplateInfoBy({
      themePages,
      templateSuffix,
    });
    if (!templateInfo) return;

    if (templateInfo.providerSource === ProviderSource.GEMPAGESV7) {
      handleEditTemplateV7({ templateInfo });
      return;
    }
    if (templateInfo.providerSource === ProviderSource.GEMPAGESV6) {
      handleEditTemplateV6({ templateInfo });
      return;
    }
  };
  return { onEditWithTemplate, onEditWithTemplateWithGPBy };
};
